// Bonus Kullanımı (sahip 2026-10-27): bonus listesinde GÖRÜŞÜLEN müşterilerden kaçı aramadan sonraki 48 saatte bonusu
// kullandı — 24/48 saat yatırım takibinin bonus karşılığı, yalnız 48 saat. Vegas / Prime / Hera: BONUS sekmesi;
// Prime VIP: RETENTION + CHURN.
//
// Veri GET /api/bonus-usage: panelin KENDİ kararı — her görüşmenin 48. saatinde müşterinin BackOffice bonus listesi bir
// kez okunur; listenin yüklü bonusunda çevrim başladıysa ya da 48 saatte yeni bonus alındıysa "kullandı". Oran =
// kullanan ÷ kontrol edilen (48 saati dolmuş ve okunmuş). Eksik değer BOŞ (§6): okunamayan 0 değildir, paydası 0 olan
// oran basılmaz, özellikten önceki günler boş.

import { useEffect, useState } from 'react';
import { getBonusUsage } from '../api';
import { BONUS_USAGE_BRANDS, brandLabel } from '../constants';
import { downloadCSV, fmtDate, fmtPct1, fmtRange, todayInTz } from '../format';
import { CsvButton, dateInputStyle, EmptyState, segStyle, Skeleton } from '../ui';
import type { BonusUsageData, BonusUsageStats, BrandId, ListDetailPeriod } from '../types';

const PERIODS: { key: ListDetailPeriod; label: string }[] = [
  { key: 'day', label: 'Günlük' },
  { key: 'week', label: 'Haftalık' },
  { key: 'month', label: 'Aylık' },
];
const WEEKDAY_TR = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
const dayLabel = (day: string) => WEEKDAY_TR[new Date(day + 'T12:00:00Z').getUTCDay()];

// Başlıklar sözcük aralarında kırılır ("KONTROL / EDİLEN", "USAGE / 48H"): iki tablo yan yana kartlara yatay kaydırmasız
// sığar — yan yana sığmayınca kartlar alt alta düşer (TABLE_CARD_BASIS), kaydırma çubuğu yalnız gerçekten dar ekranda.
// En dar tablo TR'de ~502 px (tek sözcüklü OKUNAMADI / KULLANAN / BEKLEYEN başlıkları) + kart dolgusu 38 px.
const TABLE_CARD_BASIS = '548px';
const th: React.CSSProperties = {
  fontSize: '10px',
  color: 'var(--faint)',
  letterSpacing: '.05em',
  fontWeight: 600,
  padding: '8px 6px',
  textAlign: 'right',
  verticalAlign: 'bottom',
  lineHeight: 1.35,
  borderBottom: '1px solid var(--border)',
};
const td: React.CSSProperties = {
  fontSize: '12.5px',
  color: 'var(--text)',
  padding: '8px 6px',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid var(--border)',
};
const card: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: '14px',
  padding: '16px 18px',
  marginBottom: '16px',
  minWidth: 0,
};
const PENDING_COLOR = '#e0a23c';

const HEADERS = ['GÖRÜŞME', 'KONTROL EDİLEN', 'KULLANAN', 'KULLANIM 48S', 'BEKLEYEN', 'OKUNAMADI'];
const TITLES: Record<string, string> = {
  GÖRÜŞME: 'Olumlu · Olumsuz · Aranmak İstemiyor · Konuşma Esnasında Kapattı',
  'KONTROL EDİLEN': '48 saati dolmuş ve BackOffice\'ten okunmuş',
  'KULLANIM 48S': 'Kullanan / kontrol edilen',
  BEKLEYEN: '48 saat henüz dolmadı',
  OKUNAMADI: '48 saat doldu ama BackOffice okunamadı',
};

/** Okunamama sebebi → insan dili (sunucu `unreadReason`). */
const UNREAD_REASON: Record<string, string> = {
  permission: 'BackOffice hesabının bonus listesi yetkisi yok',
  unconfigured: 'Bu markada BackOffice hesabı tanımlı değil',
  throttled: 'BackOffice şu an yanıt vermiyor (sınır / Cloudflare)',
  error: 'BackOffice okuması başarısız',
  turnover_missing: 'BackOffice çevrim bilgisini göndermedi',
};

const values = (s: BonusUsageStats) => [s.conversations, s.checked, s.used, fmtPct1(s.ratePct), s.waiting, s.unread];

function Cells({ s, bold }: { s: BonusUsageStats | null; bold?: boolean }) {
  if (!s) return <>{HEADERS.map((h) => <td key={h} style={td} />)}</>;
  return (
    <>
      {values(s).map((v, i) => (
        <td
          key={HEADERS[i]}
          title={i === 3 && s.ratePct != null ? s.used + ' / ' + s.checked : undefined}
          style={{
            ...td,
            fontWeight: bold || i === 3 ? 700 : 400,
            color: i === 5 && s.unread > 0 ? PENDING_COLOR : i === 4 || (i === 5 && !s.unread) ? 'var(--dim)' : i === 3 ? 'var(--accent)' : 'var(--text)',
            borderBottom: bold ? 'none' : td.borderBottom,
          }}
        >
          {v}
        </td>
      ))}
    </>
  );
}

function Header({ first }: { first: string }) {
  return (
    <tr>
      <th style={{ ...th, textAlign: 'left' }}>{first}</th>
      {HEADERS.map((h) => (
        <th key={h} style={th} title={TITLES[h]}>
          {h}
        </th>
      ))}
    </tr>
  );
}

function CardHead({ title, onCsv }: { title: string; onCsv?: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>{title}</span>
      {onCsv && (
        <span style={{ marginLeft: 'auto' }}>
          <CsvButton small onClick={onCsv} />
        </span>
      )}
    </div>
  );
}

function Tile({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: boolean }) {
  return (
    <div style={{ ...card, marginBottom: 0, flex: '1 1 160px' }}>
      <div style={{ fontSize: '10.5px', fontWeight: 600, letterSpacing: '.05em', color: 'var(--faint)' }}>{label}</div>
      <div style={{ fontSize: '24px', fontWeight: 700, marginTop: '6px', fontVariantNumeric: 'tabular-nums', color: accent ? 'var(--accent)' : 'var(--text)' }}>{value}</div>
      {sub && <div style={{ fontSize: '11.5px', color: 'var(--dim)', marginTop: '4px' }}>{sub}</div>}
    </div>
  );
}

export default function BonusUsage({ brands }: { brands: BrandId[] }) {
  const usable = BONUS_USAGE_BRANDS.filter((b) => brands.includes(b));
  const [brandPick, setBrandPick] = useState<BrandId | null>(null);
  const brand = brandPick && usable.includes(brandPick) ? brandPick : usable[0] || null;
  const today = todayInTz('Europe/Istanbul');
  const [period, setPeriod] = useState<ListDetailPeriod>('week');
  const [date, setDate] = useState<string>(today);
  const [data, setData] = useState<BonusUsageData | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!brand) return;
    let alive = true;
    setState('loading');
    getBonusUsage(brand, period, date).then(
      (d) => {
        if (!alive) return;
        setData(d);
        setState('ok');
      },
      () => {
        if (!alive) return;
        setData(null);
        setState('error');
      },
    );
    return () => {
      alive = false;
    };
  }, [brand, period, date, tick]);

  if (!brand) {
    return <EmptyState icon="filter_alt" title="Marka seçilmedi" reason="Üstteki marka filtresinden en az bir marka seçin." />;
  }

  const rangeLabel = data ? (data.from === data.to ? fmtDate(data.from) : fmtRange(fmtDate(data.from), fmtDate(data.to))) : '';
  const fileBase = data ? `bonus-kullanimi-${data.brand}-${data.from}_${data.to}` : 'bonus-kullanimi';
  const dayRows = data ? data.days.filter((d) => period === 'week' || !d.future) : [];
  const t = data?.total;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
        {usable.length > 1 &&
          usable.map((b) => (
            <button key={b} onClick={() => setBrandPick(b)} style={segStyle(b === brand)}>
              {brandLabel(b)}
            </button>
          ))}
        {usable.length > 1 && <span style={{ width: '6px' }} />}
        {PERIODS.map((p) => (
          <button key={p.key} onClick={() => setPeriod(p.key)} style={segStyle(p.key === period)}>
            {p.label}
          </button>
        ))}
        <input type="date" value={date} max={today} onChange={(e) => e.target.value && setDate(e.target.value)} style={dateInputStyle} />
        {data && <span style={{ fontSize: '12px', color: 'var(--faint)' }}>{rangeLabel}</span>}
        <span style={{ marginLeft: 'auto', fontSize: '11px', color: 'var(--dim)' }}>
          Kaynak: BackOffice bonus listesi, her görüşmenin 48. saatinde okunur
        </span>
      </div>

      {state === 'loading' && !data && <Skeleton heights={[96, 160, 260]} />}

      {state === 'error' && !data && (
        <div style={{ ...card, display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12.5px', color: 'var(--dim)' }}>
          <span>Bonus kullanım verisi alınamadı.</span>
          <button onClick={() => setTick((n) => n + 1)} style={{ ...segStyle(false), padding: '5px 10px', fontSize: '11.5px' }}>
            Yenile
          </button>
        </div>
      )}

      {data && t && (
        <div style={{ opacity: state === 'loading' ? 0.6 : 1, transition: 'opacity .15s' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px', fontSize: '11.5px', color: 'var(--dim)' }}>
            <span>
              Kullandı = aramadan sonraki 48 saatte listenin yüklü bonusunda çevrim başladı ya da yeni bir bonus alındı. Oran
              yalnız 48 saati dolmuş görüşmeler üzerinden.
            </span>
            {!data.since && <span style={{ color: PENDING_COLOR }}>Henüz veri yok — ilk kontroller birkaç dakika içinde başlar.</span>}
            {data.since && data.since > data.from && (
              <span>
                <span>Veri başlangıcı</span> <span>{fmtDate(data.since)}</span>
              </span>
            )}
            {t.unread > 0 && (
              <span style={{ color: PENDING_COLOR }}>
                <span>{t.unread}</span> <span>görüşme okunamadı</span>
                {UNREAD_REASON[data.unreadReason] && (
                  <>
                    {' — '}
                    <span>{UNREAD_REASON[data.unreadReason]}</span>
                  </>
                )}
              </span>
            )}
            {t.late > 0 && (
              <span>
                <span>{t.late}</span> <span>görüşme 48 saatten sonra okundu (ilk kurulum) — sonradan kullanım da sayılmış olabilir.</span>
              </span>
            )}
          </div>

          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <Tile label="GÖRÜŞME" value={String(t.conversations)} />
            <Tile label="KULLANAN" value={String(t.used)} sub={t.checked ? t.used + ' / ' + t.checked : undefined} />
            <Tile label="KULLANIM 48S" value={fmtPct1(t.ratePct)} accent />
            <Tile label="BEKLEYEN" value={String(t.waiting)} sub="48 saat henüz dolmadı" />
          </div>

          {/* 1 · Listeler (Prime VIP'te RETENTION + CHURN; diğerlerinde tek BONUS satırı). */}
          <div style={card}>
            <CardHead
              title="Listeler"
              onCsv={() =>
                downloadCSV(fileBase + '.csv', [
                  ['LİSTE', ...HEADERS],
                  ...data.lists.map((l) => [l.tab, ...values(l.totals)]),
                  ['Toplam', ...values(t)],
                ])
              }
            />
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <Header first="LİSTE" />
                </thead>
                <tbody>
                  {data.lists.map((l) => (
                    <tr key={l.tab}>
                      <td style={{ ...td, textAlign: 'left', fontWeight: 600, color: 'var(--dim)' }}>{l.tab}</td>
                      <Cells s={l.totals} />
                    </tr>
                  ))}
                  {data.lists.length > 1 && (
                    <tr>
                      <td style={{ ...td, textAlign: 'left', fontWeight: 700, borderBottom: 'none' }}>Toplam</td>
                      <Cells s={t} bold />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 16px', alignItems: 'flex-start' }}>
            {/* 2 · Günlere göre (bütün bonus listeleri). */}
            <div style={{ ...card, flex: '1 1 ' + TABLE_CARD_BASIS }}>
              <CardHead
                title="Günlere göre"
                onCsv={() =>
                  downloadCSV(fileBase + '-gunler.csv', [
                    ['GÜN', ...HEADERS],
                    ...dayRows.map((d) => [d.day, ...(d.stats ? values(d.stats) : [])]),
                    ['Toplam', ...values(t)],
                  ])
                }
              />
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <Header first="GÜN" />
                  </thead>
                  <tbody>
                    {dayRows.map((d) => (
                      <tr
                        key={d.day}
                        title={!d.future && !d.covered ? 'Bu gün özellik başlamadan önce' : undefined}
                        style={{ background: d.day === date ? 'var(--accent-soft)' : 'transparent', opacity: d.future ? 0.45 : 1 }}
                      >
                        <td style={{ ...td, textAlign: 'left', color: 'var(--dim)' }}>
                          <span style={{ fontWeight: 600 }}>{dayLabel(d.day)}</span>
                          <span style={{ marginLeft: '8px', color: 'var(--faint)' }}>{fmtDate(d.day).slice(0, 5)}</span>
                        </td>
                        <Cells s={d.stats} />
                      </tr>
                    ))}
                    <tr>
                      <td style={{ ...td, textAlign: 'left', fontWeight: 700, borderBottom: 'none' }}>Toplam</td>
                      <Cells s={t} bold />
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* 3 · Temsilcilere göre. */}
            <div style={{ ...card, flex: '1 1 ' + TABLE_CARD_BASIS }}>
              <CardHead
                title="Temsilcilere göre"
                onCsv={() =>
                  downloadCSV(fileBase + '-temsilciler.csv', [
                    ['TEMSİLCİ', ...HEADERS],
                    ...data.agents.map((a) => [a.name, ...values(a)]),
                    ['Toplam', ...values(t)],
                  ])
                }
              />
              {data.agents.length === 0 ? (
                <div style={{ fontSize: '12px', color: 'var(--dim)', padding: '6px 0' }}>Bu dönemde bonus listesinde görüşme yok.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <Header first="TEMSİLCİ" />
                    </thead>
                    <tbody>
                      {data.agents.map((a) => (
                        <tr key={a.agentId}>
                          <td style={{ ...td, textAlign: 'left', fontWeight: 600, color: 'var(--dim)', whiteSpace: 'normal' }}>{a.name}</td>
                          <Cells s={a} />
                        </tr>
                      ))}
                      <tr>
                        <td style={{ ...td, textAlign: 'left', fontWeight: 700, borderBottom: 'none' }}>Toplam</td>
                        <Cells s={t} bold />
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
